// Account members constants — settings account members list table.

/** Client-side duplicate-email match state for the add-member modal. */
export const DUPLICATE_MEMBER_STATE = Object.freeze({
  PENDING: 'pending',
  ACTIVE: 'active',
})

/** Backend error codes for POST /users/add (HTTP 409). */
export const ADD_ACCOUNT_MEMBER_ERROR_CODE = Object.freeze({
  EMAIL_IN_USE_ELSEWHERE: 'email_in_use_elsewhere',
  EMAIL_ALREADY_EXISTS_IN_ACCOUNT: 'email_already_exists_in_account',
})

/** Backend error codes for POST /users/remove (HTTP 404 / 409 / 403). */
export const REMOVE_ACCOUNT_MEMBER_ERROR_CODE = Object.freeze({
  USER_NOT_FOUND: 'user_not_found',
  LAST_ADMIN: 'last_admin',
  CANNOT_TARGET_SELF: 'cannot_target_self',
})

/** Status filter "All" value and URL default when no status filter is active. */
export const ACCOUNT_MEMBER_STATUS_ALL_KEY = 'all'

export const ACCOUNT_MEMBER_STATUS = Object.freeze({
  ACTIVE: 'active',
  PENDING: 'pending',
})

/** Valid status filter values for URL ?status validation (excludes "all"). */
export const VALID_ACCOUNT_MEMBER_STATUS_KEYS = new Set([
  ACCOUNT_MEMBER_STATUS.ACTIVE,
  ACCOUNT_MEMBER_STATUS.PENDING,
])

export const ACCOUNT_MEMBER_STATUS_FILTER_OPTIONS = [
  { value: ACCOUNT_MEMBER_STATUS_ALL_KEY, label: 'All', icon: 'users' },
  { value: ACCOUNT_MEMBER_STATUS.ACTIVE, label: 'Active', icon: 'circle-check' },
  { value: ACCOUNT_MEMBER_STATUS.PENDING, label: 'Pending', icon: 'clock' },
]

export const ACCOUNT_MEMBERS_TABLE_COLUMN_NAMES = {
  NAME: 'name',
  ROLE: 'role',
  STATUS: 'status',
  LAST_LOGIN: 'lastLogin',
  ACTIONS: 'actions',
}

/**
 * Default table state for URL sync (`getSortAndPaginationFromQuery`).
 * Account members list URL carries sort + page + filter params.
 * `sortBy` must be the `name` of a sortable entry in `ACCOUNT_MEMBERS_TABLE_COLUMNS`.
 */
export const ACCOUNT_MEMBERS_DEFAULT_SORT = {
  sortBy: ACCOUNT_MEMBERS_TABLE_COLUMN_NAMES.NAME,
  descending: false,
  page: 1,
  rowsPerPage: 10,
}

/** Default filter shape for URL sync (`getFilterFromQuery` / `sanitizeQuery`). */
export const ACCOUNT_MEMBERS_DEFAULT_FILTER = {
  search: '',
  status: ACCOUNT_MEMBER_STATUS_ALL_KEY,
}

/** Combined sort + filter defaults for URL sanitize. */
export const ACCOUNT_MEMBERS_QUERY_DEFAULTS = {
  ...ACCOUNT_MEMBERS_DEFAULT_SORT,
  ...ACCOUNT_MEMBERS_DEFAULT_FILTER,
}

export const ACCOUNT_MEMBERS_TABLE_COLUMNS = [
  {
    name: ACCOUNT_MEMBERS_TABLE_COLUMN_NAMES.NAME,
    label: 'Name & email',
    field: 'fullName',
    align: 'left',
    sortable: true,
    classes: 'account-member-name main-td',
    headerClasses: 'account-member-name main-td',
  },
  {
    name: ACCOUNT_MEMBERS_TABLE_COLUMN_NAMES.ROLE,
    label: 'Role',
    field: 'role',
    align: 'left',
    sortable: true,
    classes: 'account-member-role sm-td',
    headerClasses: 'account-member-role sm-td',
  },
  {
    name: ACCOUNT_MEMBERS_TABLE_COLUMN_NAMES.STATUS,
    label: 'Status',
    field: 'isActive',
    align: 'center',
    sortable: true,
    classes: 'account-member-status md-td',
    headerClasses: 'account-member-status md-td',
  },
  {
    name: ACCOUNT_MEMBERS_TABLE_COLUMN_NAMES.LAST_LOGIN,
    label: 'Last login',
    field: 'lastLoginAt',
    align: 'right',
    sortable: true,
    classes: 'account-member-last-login md-td',
    headerClasses: 'account-member-last-login md-td',
  },
  {
    name: ACCOUNT_MEMBERS_TABLE_COLUMN_NAMES.ACTIONS,
    label: '',
    field: 'actions',
    align: 'right',
    classes: 'actions',
    headerClasses: 'actions',
  },
]

/** BasicNoData config for the account members table. */
export const NO_DATA_ACCOUNT_MEMBERS = {
  empty: {
    icon: 'users',
    title: 'No members found',
    message: 'No account members to display.',
  },
  filtered: {
    icon: 'search-x',
    title: 'No members match your filters',
    message: 'Try a different search or status.',
    clearActionLabel: 'Clear filters',
  },
}
